import React from "react";
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    Divider,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import {
    TEST_TYPE_LABELS,
    DIFFICULTY_LABELS,
    formatDate,
    formatAnswer,
    formatTime,
} from "../../utils/ettReportFormat";

// Presentational ETT exam-report body: header + score summary + per-section question
// breakdown. Pure function of `report`. Shared by the ETT Reports page (wrapped with a ref
// for PDF export), the classroom report page, and the prep-program exam-matrix modal.
// forwardRef → the page attaches its PDF-capture ref to the printable root. `sectionFilter`
// optionally narrows the section breakdown (e.g. the classroom page shows only math/english);
// the score summary always reflects the full report.
const EttReportView = React.forwardRef(({ report, sectionFilter = () => true }, ref) => {
    if (!report) return null;

    const getQuestionCountByScoreIndex = (scoreIndex) => {
        const type = report.test_type;
        if (type === 0 || type === 3 || type === 20 || type === 23) {
            // SAT/PSAT: score_1 = English (module_num <= 3), score_2 = Math (module_num >= 4)
            if (scoreIndex === 0) {
                return report.session_sections.filter((s) => s.module_num <= 3).reduce((sum, s) => sum + s.session_questions.length, 0);
            } else if (scoreIndex === 1) {
                return report.session_sections.filter((s) => s.module_num >= 4).reduce((sum, s) => sum + s.session_questions.length, 0);
            }
        }
        // ACT and others: group by unique subject in order
        const subjectOrder = [];
        for (const s of report.session_sections) {
            if (!subjectOrder.includes(s.subject)) subjectOrder.push(s.subject);
        }
        const subject = subjectOrder[scoreIndex];
        if (!subject) return 0;
        return report.session_sections.filter((s) => s.subject === subject).reduce((sum, s) => sum + s.session_questions.length, 0);
    };

    const getScoreSummary = () => {
        const type = report.test_type;
        if (type === 1) {
            // ACT
            const scores = [report.score_1, report.score_2, report.score_3, report.score_4].filter((s) => s > 0);
            const composite = scores.length > 0 ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length) : 0;
            return [
                { label: "Composite", score: composite, raw: null, totalQ: null },
                { label: "English", score: report.score_1, raw: report.raw_score_1, totalQ: getQuestionCountByScoreIndex(0) },
                { label: "Math", score: report.score_2, raw: report.raw_score_2, totalQ: getQuestionCountByScoreIndex(1) },
                { label: "Reading", score: report.score_3, raw: report.raw_score_3, totalQ: getQuestionCountByScoreIndex(2) },
                { label: "Science", score: report.score_4, raw: report.raw_score_4, totalQ: getQuestionCountByScoreIndex(3) },
            ];
        }
        // SAT/PSAT/others
        const total = (report.score_1 || 0) + (report.score_2 || 0);
        return [
            { label: "Total", score: total, raw: null, totalQ: null },
            { label: "Reading & Writing", score: report.score_1, raw: report.raw_score_1, totalQ: getQuestionCountByScoreIndex(0) },
            { label: "Math", score: report.score_2, raw: report.raw_score_2, totalQ: getQuestionCountByScoreIndex(1) },
        ];
    };

    const getSectionStats = (section) => {
        const total = section.session_questions.length;
        const correct = section.session_questions.filter((q) => q.result).length;
        // ratio is null for an empty section so the chip shows neutral grey instead of a
        // NaN comparison silently falling through to "failing" red.
        return { total, correct, incorrect: total - correct, ratio: total ? correct / total : null };
    };

    const scoreSummary = getScoreSummary();

    return (
        <Box ref={ref} sx={{ backgroundColor: "#fff", color: "#000", p: "30px", borderRadius: "8px", maxWidth: "1100px", mx: "auto" }}>
            {/* Header */}
            <Box mb="25px">
                <Typography variant="h4" fontWeight="bold" color="#000" gutterBottom>
                    {report.test_title}
                </Typography>
                <Typography variant="body1" color="#333">
                    Student: {report.student?.first_name} {report.student?.last_name} ({report.student?.email})
                </Typography>
                <Typography variant="body2" color="#666">
                    Date: {formatDate(report.finished_at)} | Type: {TEST_TYPE_LABELS[report.test_type] || `Type ${report.test_type}`}
                    {report.is_adaptive && " | Adaptive"}
                </Typography>
            </Box>

            {/* Score Summary */}
            <Box mb="25px">
                <Typography variant="h6" fontWeight="bold" color="#000" mb="10px">
                    Score Summary
                </Typography>
                <Box display="flex" gap="20px" flexWrap="wrap">
                    {scoreSummary.map((item) => (
                        <Paper
                            key={item.label}
                            elevation={2}
                            sx={{
                                p: "15px 25px",
                                textAlign: "center",
                                minWidth: "120px",
                                backgroundColor: item.raw === null ? "#1976d2" : "#f5f5f5",
                                color: item.raw === null ? "#fff" : "#000",
                            }}
                        >
                            <Typography variant="h4" fontWeight="bold">{item.score}</Typography>
                            <Typography variant="body2">{item.label}</Typography>
                            {item.raw !== null && (
                                <Typography variant="caption" color="#888">
                                    Raw: {item.raw} out of {item.totalQ}
                                </Typography>
                            )}
                        </Paper>
                    ))}
                </Box>
            </Box>

            <Divider sx={{ mb: "25px" }} />

            {/* Section Breakdown */}
            {report.session_sections.filter(sectionFilter).map((section) => {
                const stats = getSectionStats(section);
                return (
                    <Box key={section.id} mb="25px">
                        <Box display="flex" alignItems="center" gap="15px" mb="10px">
                            <Typography variant="h6" fontWeight="bold" color="#000">{section.subject}</Typography>
                            <Chip
                                label={`${stats.correct}/${stats.total} correct`}
                                size="small"
                                sx={{
                                    backgroundColor: stats.ratio === null ? "#9e9e9e" : stats.ratio >= 0.7 ? "#4caf50" : stats.ratio >= 0.4 ? "#ff9800" : "#f44336",
                                    color: "#fff",
                                }}
                            />
                        </Box>
                        <TableContainer component={Paper} variant="outlined">
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ backgroundColor: "#f0f0f0" }}>
                                        <TableCell sx={{ fontWeight: "bold", color: "#000" }}>#</TableCell>
                                        <TableCell sx={{ fontWeight: "bold", color: "#000" }}>Result</TableCell>
                                        <TableCell sx={{ fontWeight: "bold", color: "#000" }}>Student Answer</TableCell>
                                        <TableCell sx={{ fontWeight: "bold", color: "#000" }}>Correct Answer</TableCell>
                                        <TableCell sx={{ fontWeight: "bold", color: "#000" }}>Difficulty</TableCell>
                                        <TableCell sx={{ fontWeight: "bold", color: "#000" }}>Time</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {section.session_questions.map((q) => (
                                        <TableRow
                                            key={q.id}
                                            sx={{ backgroundColor: q.result ? "#e8f5e9" : q.is_omitted ? "#fff8e1" : "#ffebee" }}
                                        >
                                            <TableCell sx={{ color: "#000" }}>{q.question_num}</TableCell>
                                            <TableCell>
                                                {q.result ? (
                                                    <CheckCircleIcon sx={{ color: "#4caf50", fontSize: 20 }} />
                                                ) : q.is_omitted ? (
                                                    <Typography variant="caption" color="#ff9800">Omitted</Typography>
                                                ) : (
                                                    <CancelIcon sx={{ color: "#f44336", fontSize: 20 }} />
                                                )}
                                            </TableCell>
                                            <TableCell sx={{ color: "#000" }}>
                                                {q.user_multiselect_answer?.length > 0
                                                    ? q.user_multiselect_answer.map((a) => formatAnswer(a, q.answer_type)).join(", ")
                                                    : formatAnswer(q.user_answer, q.answer_type)}
                                            </TableCell>
                                            <TableCell sx={{ color: "#000" }}>{formatAnswer(q.correct_answer, q.answer_type)}</TableCell>
                                            <TableCell sx={{ color: "#000" }}>{DIFFICULTY_LABELS[q.difficulty] || "-"}</TableCell>
                                            <TableCell sx={{ color: "#000" }}>{formatTime(q.time_user_spent)}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                );
            })}
        </Box>
    );
});

EttReportView.displayName = "EttReportView";

export default EttReportView;
